<template>
  <div class="filtro-page">
    <!-- Header Links Component -->
    <FiltroHeaderLinks 
      :containerId="headerContainerId"
      @open-modal="openModal"
    />
    
    <!-- Modal Component -->
    <FiltroModal 
      ref="filtroModalRef"
      :componentId="componentId"
      :dateRangePickerConfig="dateRangePickerConfig"
      @filter-applied="handleFilterApplied"
      @filter-error="handleFilterError"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useFiltro } from '../../composables/useFiltro';
import { log } from '../../utils/DebugLogger';
import FiltroModal from './FiltroModal.vue';
import FiltroHeaderLinks from './FiltroHeaderLinks.vue';

interface FiltroModalInstance {
  openModal: () => void;
}

interface Props {
  componentId?: string;
  dateRangePickerConfig?: Record<string, unknown>;
  headerContainerId?: string;
  autoInit?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  componentId: () => `filtro_${Date.now()}`,
  dateRangePickerConfig: () => ({}),
  headerContainerId: 'filtroContainer',
  autoInit: true
});

const emit = defineEmits<{
  'filter-ready': [payload: Record<string, unknown>];
  'filter-updated': [payload: Record<string, unknown>];
  'filter-applied': [payload: Record<string, unknown>];
  'filter-error': [error: unknown];
}>();

const filtroModalRef = ref<FiltroModalInstance | null>(null);

const {
  filterData,
  selectedFranchises,
  selectedPeriod,
  showDateAlert,
  initialized,
  initializing,
  loading,
  selectedFranchisesCount,
  init,
  getSelectedFranchisesArray,
  getSelectedFranchisesSelectArray
} = useFiltro({ dateRangePickerConfig: props.dateRangePickerConfig });

const openModal = (): void => {
  log('FiltroPage', 'openModal', 'Opening filter modal');
  if (filtroModalRef.value) {
    filtroModalRef.value.openModal();
  } else {
    log('FiltroPage', 'openModal', 'Modal ref not available');
  }
};

const handleFilterUpdated = (data: Record<string, unknown>): void => {
  emit('filter-updated', data);
};

const handleFilterApplied = (data: Record<string, unknown>): void => {
  emit('filter-applied', data);
  emit('filter-ready', {
    filterData: filterData.value,
    selectedFranchises: selectedFranchises.value,
    selectedPeriod: selectedPeriod.value,
    showDateAlert: showDateAlert.value
  });
};

const handleFilterError = (error: unknown): void => {
  emit('filter-error', error);
};

const getFilterData = () => {
  return filterData.value;
};

onMounted(() => {
  log('FiltroPage', 'mounted', 'Component mounted, setting up...');
  if (props.autoInit) {
    log('FiltroPage', 'mounted', 'Auto-init enabled, initializing...');
    log('FiltroPage', 'mounted', 'initialized.value:', initialized.value);
    init();
  }
});

onBeforeUnmount(() => {
  log('FiltroPage', 'beforeUnmount', 'Cleaning up');
});

defineExpose({
  openModal,
  getFilterData,
  getSelectedFranchisesArray,
  getSelectedFranchisesSelectArray,
  init
});
</script>

<style scoped>
.filtro-page {
  /* Container for the complete filter functionality */
  position: relative;
}
</style>
